// The sign-up wizard's rules, kept pure (signupModel.selftest.ts): which
// screen a person is on, what each refusal says and the one thing that
// helps, the codes the Google callback leaves in the address bar, and the
// first guesses for the fields.
//
// The server's steps (routes/signup.py): work_check → details → waiting →
// building → ready, or declined. "details" is three screens here (About
// you, Your assistant, Review), each saved as it is left.

import type { SignupDetails, SignupMe } from "@/api/signup";

export type DetailScreen = "about" | "assistant" | "review";
export type Screen = "start" | "work" | DetailScreen | "waiting" | "building" | "ready" | "declined";

/** The first details screen with something still to fill in. */
export function firstDetailScreen(d: SignupDetails): DetailScreen {
  if (!filled(d.your_name) || !filled(d.workspace_name)) return "about";
  if (!filled(d.agent_name) || !filled(d.agent_role)) return "assistant";
  return "review";
}

/** What the person chose to look at while filling in: a details screen, or back to "work". */
export type LocalScreen = DetailScreen | "work";

/** Where the person is: no sign-up yet is the start; "details" is the screen they chose (or the first unfilled one). */
export function screenOf(me: SignupMe | null, local: LocalScreen | null): Screen {
  if (!me) return "start";
  switch (me.step) {
    case "work_check":
      return "work";
    case "details":
      if (local === "work") return hasWorkStep(me) ? "work" : firstDetailScreen(me.details);
      return local ?? firstDetailScreen(me.details);
    default:
      return me.step;
  }
}

/** The steps a person goes through after Google, for the progress bar; the "work" step only for a personal account on a personal address. */
export function stepsFor(me: SignupMe): Screen[] {
  const steps: Screen[] = ["about", "assistant", "review"];
  return hasWorkStep(me) ? ["work", ...steps] : steps;
}

export function hasWorkStep(me: Pick<SignupMe, "kind" | "email" | "work_domain">): boolean {
  return me.kind === "personal" && domainOf(me.email) !== (me.work_domain ?? "").toLowerCase();
}

/** A personal Google account on its own work address: that domain, which is the only one its work check may be made on; else null. */
export function ownDomainOf(me: Pick<SignupMe, "kind" | "email" | "work_domain">): string | null {
  const own = domainOf(me.email);
  return me.kind === "personal" && own && own === (me.work_domain ?? "").toLowerCase() ? own : null;
}

/** {index (1-based), total} for the bar, or null on a screen that is not a step. */
export function progressOf(me: SignupMe | null, screen: Screen): { index: number; total: number } | null {
  if (!me) return null;
  const steps = stepsFor(me);
  const i = steps.indexOf(screen);
  return i < 0 ? null : { index: i + 1, total: steps.length };
}

/** How often to look again: waiting for an admin is slow, a build is not. */
export function pollMs(step: SignupMe["step"] | null): number | null {
  if (step === "waiting") return 20_000;
  if (step === "building") return 5_000;
  return null;
}

// ─── refusals ───

export type RefusalAction =
  | { kind: "google"; label: string; hd?: string; hint?: string }
  | { kind: "signin"; label: string };

export interface Refusal {
  code: string;
  message: string;
  action: RefusalAction | null;
}

const ANOTHER: RefusalAction = { kind: "google", label: "Use another account" };
const AGAIN: RefusalAction = { kind: "google", label: "Try again" };
const SIGN_IN: RefusalAction = { kind: "signin", label: "Go to sign in" };

/** One plain line and the one action that helps, for a code the server or the callback gave. */
export function refusalOf(code: string, domain?: string | null, hint?: string | null): Refusal {
  const d = (domain ?? "").trim().toLowerCase();
  const r = (message: string, action: RefusalAction | null): Refusal => ({ code, message, action });
  switch (code) {
    case "uses_google_workspace":
      return d
        ? r(`${d} uses Google Workspace. Sign up with your ${d} account.`, {
            kind: "google",
            label: `Sign up with ${d}`,
            hd: d,
            ...(hint ? { hint } : {}),
          })
        : r("Your work uses Google Workspace. Sign up with your work account.", ANOTHER);
    case "org_exists":
      return r(`${d || "Your company"} already has a Corvus workspace. Ask its owner to add you.`, SIGN_IN);
    case "email_unverified":
      return r("Google hasn't verified this email. Use another account.", ANOTHER);
    case "agent_account":
      return r("Corvus already uses this Google account for an assistant. Use your own.", ANOTHER);
    case "email_taken":
      return r("This email already has a Corvus login. Sign in with your password.", SIGN_IN);
    case "login_disabled":
      return r("This login has been switched off. Ask whoever runs your workspace.", null);
    case "domain_unknown":
      return r(d ? `We couldn't find ${d}. Check the spelling.` : "We couldn't find that domain.", ANOTHER);
    case "check_failed":
      return r("We couldn't check your work's email just now. Try again.", AGAIN);
    case "check_own_domain":
      return r(d ? `We need to check ${d} first. Try again.` : "We need to check your work's email first. Try again.", AGAIN);
    case "consumer_domain":
      return r(d ? `${d} is a personal email. Enter where you work.` : "That's a personal email. Enter where you work.", null);
    case "bad_domain":
      return r("Enter a work email or domain, like name@company.com.", null);
    case "already_open":
      return r("You've already opened Corvus. Sign in with Google.", SIGN_IN);
    case "cancelled":
      return r("Google sign-in was cancelled.", AGAIN);
    case "too_many":
      return r("Too many tries. Wait a few minutes, then try again.", AGAIN);
    case "signup_closed":
      return r("Sign-ups aren't open right now.", SIGN_IN);
    case "signin_off":
      return r("Sign in with Google is switched off.", SIGN_IN);
    case "google_unavailable":
      return r("Google sign-in isn't available right now.", SIGN_IN);
    case "expired":
      return r("That took too long. Start again.", AGAIN);
    default:
      return r("Google sign-in didn't work. Try again.", AGAIN);
  }
}

/** The code the callback put in the fragment (#e=<code>[&d=<domain>]), or null. */
export function fragmentRefusal(hash: string): { code: string; domain: string | null } | null {
  const q = new URLSearchParams(hash.replace(/^#/, ""));
  const code = q.get("e");
  if (!code || !/^[a-z_]{1,40}$/.test(code)) return null;
  const domain = q.get("d");
  return { code, domain: domain && /^[a-z0-9.-]{1,253}$/i.test(domain) ? domain.toLowerCase() : null };
}

/** The login page's line for the callback's #google=<code>, or null for none. */
export function googleSignInError(hash: string): string | null {
  const code = new URLSearchParams(hash.replace(/^#/, "")).get("google");
  if (!code) return null;
  switch (code) {
    case "no_login":
      return "No Corvus login uses this Google account.";
    case "disabled":
      return "This login has been switched off. Ask whoever runs your workspace.";
    case "not_ready":
      return "Your workspace isn't ready yet.";
    case "email_unverified":
      return "Google hasn't verified this email. Use another account.";
    case "cancelled":
      return "Google sign-in was cancelled.";
    case "expired":
      return "That sign-in took too long. Try again.";
    case "too_many":
      return "Too many tries. Wait a few minutes, then try again.";
    case "signin_off":
      return "Sign in with Google is switched off.";
    case "google_unavailable":
      return "Google sign-in isn't available right now.";
    case "not_here":
      return "Sign in with Google isn't allowed from this network.";
    default:
      return "Google sign-in didn't work. Try again.";
  }
}

// ─── the work check ───

/** A field error for "Where do you work?", or null when the input may be sent. */
export function workInputProblem(text: string): string | null {
  const t = text.trim();
  if (!t) return "Enter your work email or your company's domain.";
  const domain = t.includes("@") ? t.slice(t.lastIndexOf("@") + 1) : t.replace(/^https?:\/\//i, "").split("/")[0];
  if (!/^[a-z0-9-]+(\.[a-z0-9-]+)+$/i.test(domain)) return "Enter a work email or domain, like name@company.com.";
  return null;
}

/** Codes from POST /work-check that belong under the field (the others are the step's line). */
export const WORK_FIELD_CODES = new Set(["bad_domain", "consumer_domain", "domain_unknown"]);

// ─── first guesses ───

export function domainOf(email: string): string {
  const at = email.lastIndexOf("@");
  return at < 0 ? "" : email.slice(at + 1).trim().toLowerCase();
}

/** "acme-labs.co.uk" → "Acme Labs": a starting name for the workspace from the work domain, "" without one. */
export function workspaceNameGuess(me: Pick<SignupMe, "hosted_domain" | "work_domain">): string {
  const domain = (me.hosted_domain || me.work_domain || "").trim().toLowerCase();
  const label = domain.split(".")[0] ?? "";
  if (!label) return "";
  return label
    .split(/[-_]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

/** The Review's "Work" row: the work domain, "No work email", or null (a Workspace account: its email says it). */
export function workLine(me: Pick<SignupMe, "kind" | "hosted_domain" | "work_domain">): string | null {
  if (me.kind === "workspace") return null;
  return me.work_domain || "No work email";
}

/** "Northwind · London (GMT)": the Review's workspace row, the zone as `zoneText` writes it. */
export function workspaceLine(name: string | null | undefined, zone: string): string {
  return [name?.trim(), zone].filter(Boolean).join(" · ");
}

/** The fields each details screen saves (Back saves them too, so Review always shows what was saved). */
export const SCREEN_FIELDS: Record<"about" | "assistant", (keyof SignupDetails)[]> = {
  about: ["your_name", "workspace_name", "timezone"],
  assistant: ["agent_name", "agent_role"],
};

/** The fields a details screen needs before Continue, as {field: line}. */
export function detailsProblems(screen: DetailScreen, d: SignupDetails): Partial<Record<keyof SignupDetails, string>> {
  const out: Partial<Record<keyof SignupDetails, string>> = {};
  if (screen === "about") {
    if (!filled(d.your_name)) out.your_name = "Enter your name.";
    if (!filled(d.workspace_name)) out.workspace_name = "Name your workspace.";
  }
  if (screen === "assistant") {
    if (!filled(d.agent_name)) out.agent_name = "Give your assistant a name.";
    if (!filled(d.agent_role)) out.agent_role = "Pick what it helps with.";
  }
  return out;
}

/** The server's field for a details refusal, so its line sits under that field. */
export function detailsFieldOf(code: string | null): keyof SignupDetails | null {
  if (code === "bad_agent_name") return "agent_name";
  if (code === "bad_role") return "agent_role";
  if (code === "bad_timezone") return "timezone";
  return null;
}

function filled(v: string | null | undefined): boolean {
  return typeof v === "string" && v.trim().length > 0;
}
